import React, { useState, useCallback, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import KanbanCard from '../kanban/KanbanCard';
import TaskDetailsModal from '../kanban/TaskDetailsModal';
import { useKanbanAdmin } from '../../contexts/KanbanAdminContext';
import { Tarefa, StatusTarefa, Usuario, Departamento } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { colunasRecebidas } from '../../data/mockData';
import { toast } from 'react-toastify';
import { Button, IconButton, Tooltip, TextField, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import usuarioService from '../../services/usuarioService';
import Spinner from '../common/Spinner';
import departamentoService from '../../services/departamentoService';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate } from 'react-router-dom';
import { mapearComentarios } from '../tarefas/ComentarioMapper';
import tarefaAdminService from '../../services/tarefaAdminService';

const KanbanAdminBoard: React.FC = () => {
  const { quadro, tarefas, carregando, recarregarTarefas, moverTarefa, atualizarTarefa, filtros, setFiltros, totalTarefas } = useKanbanAdmin();
  const { usuario } = useAuth();
  const [tarefaSelecionada, setTarefaSelecionada] = useState<Tarefa | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtrosTemp, setFiltrosTemp] = useState(filtros);
  const [usuariosDropdown, setUsuariosDropdown] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);
  const navigate = useNavigate();
  
  // Referência para o container de scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Estado para controlar o arrasto para scroll
  const [isDraggingScroll, setIsDraggingScroll] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Função para lidar com o fim do arrasto de uma tarefa
  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    // Se não houver destino ou se o destino for o mesmo que a origem, não fazer nada
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }
    
    // Encontrar a coluna de origem e destino
    const colunaOrigem = quadro.colunas.find(col => col.id === source.droppableId);
    const colunaDestino = quadro.colunas.find(col => col.id === destination.droppableId);
    
    if (!colunaOrigem || !colunaDestino) return;
    
    // Encontrar a tarefa que está sendo arrastada
    const tarefa = colunaOrigem.tarefas.find(t => t.id === draggableId);
    if (!tarefa) return;
    
    try {
      // Atualizar o status da tarefa
      const novoStatus = colunaDestino.titulo as StatusTarefa;
      
      // Atualizar a tarefa no contexto com o novo status
      const tarefaAtualizada = { ...tarefa, status: novoStatus };
      
      // Usar apenas atualizarTarefa que já faz a atualização local e na API
      await atualizarTarefa(tarefaAtualizada);
      
      toast.success(`Tarefa movida para ${novoStatus}`);
    } catch (error) {
      console.error('Erro ao atualizar status da tarefa:', error);
      toast.error('Erro ao mover tarefa. Tente novamente.');
      
      // Recarregar tarefas para garantir consistência
      await recarregarTarefas();
    }
  };

  // Carregar opções de usuários e departamentos para filtros (admin)
  useEffect(() => {
    const carregarOpcoes = async () => {
      try {
        setCarregandoOpcoes(true);

        const [respUsuarios, respDepartamentos] = await Promise.all([
          usuarioService.obterParaDropdown(),
          departamentoService.obterTodos()
        ]);

        if (respUsuarios.success && respUsuarios.usuarios) {
          setUsuariosDropdown(respUsuarios.usuarios);
        }

        if (respDepartamentos.success && respDepartamentos.departamentos) {
          setDepartamentos(respDepartamentos.departamentos);
        }
      } catch (error) {
        console.error('Erro ao carregar opções de filtro (admin):', error);
        toast.error('Erro ao carregar usuários/departamentos');
      } finally {
        setCarregandoOpcoes(false);
      }
    };

    carregarOpcoes();
  }, []);

  // Função para obter o valor numérico da prioridade para ordenação
  const obterValorPrioridade = (prioridade: string): number => {
    const prioridades: Record<string, number> = {
      'alta': 0,
      'urgente': 0,
      'media': 1,
      'média': 1,
      'baixa': 2
    };
    return prioridades[prioridade.toLowerCase()] || 3;
  };

  const abrirDetalhes = async (tarefa: Tarefa) => {
    if (!tarefa || !tarefa.id) {
      console.error('Tentativa de abrir detalhes de tarefa inválida');
      return;
    }
    
    try {
      // Usar o serviço admin para buscar dados atualizados da tarefa
      console.log(`Buscando detalhes da tarefa ${tarefa.id} usando serviço admin`);
      const tarefaAtualizada = await tarefaAdminService.obterTarefa(tarefa.id);
      
      // Verificar se a tarefa atualizada é válida e tem as propriedades necessárias
      if (tarefaAtualizada && tarefaAtualizada.id) {
        // Garantir que a tarefa tenha todas as propriedades necessárias
        const tarefaCompleta = {
          ...tarefa, // Manter os dados originais como fallback
          ...tarefaAtualizada, // Sobrescrever com os dados atualizados
          // Garantir que propriedades críticas existam
          responsavel: tarefaAtualizada.responsavel || tarefa.responsavel || { id: '', nome: 'Não atribuído' },
          criador: tarefaAtualizada.criador || tarefa.criador || { id: '', nome: 'Desconhecido' },
          // Mapear os comentários usando o utilitário de mapeamento
          comentarios: mapearComentarios(tarefaAtualizada.comentarios || [])
        };
        
        console.log('Tarefa carregada com sucesso (visão admin):', tarefaCompleta);
        setTarefaSelecionada(tarefaCompleta);
        setModalAberto(true);
      } else {
        // Fallback para os dados existentes caso a API falhe
        // Garantir que a tarefa original tenha as propriedades necessárias
        const tarefaSegura = {
          ...tarefa,
          responsavel: tarefa.responsavel || { id: '', nome: 'Não atribuído' },
          criador: tarefa.criador || { id: '', nome: 'Desconhecido' },
          comentarios: tarefa.comentarios || []
        };
        
        setTarefaSelecionada(tarefaSegura);
        setModalAberto(true);
        toast.warning('Alguns detalhes da tarefa podem estar desatualizados');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da tarefa (visão admin):', error);
      
      // Mesmo com erro, mostrar os dados que temos
      const tarefaSegura = {
        ...tarefa,
        responsavel: tarefa.responsavel || { id: '', nome: 'Não atribuído' },
        criador: tarefa.criador || { id: '', nome: 'Desconhecido' },
        comentarios: tarefa.comentarios || []
      };
      
      setTarefaSelecionada(tarefaSegura);
      setModalAberto(true);
      toast.error('Erro ao carregar detalhes atualizados da tarefa');
    }
  };

  // Manipulador para iniciar o arrasto para scroll
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Obter o elemento alvo do evento
    const target = e.target as HTMLElement;
    
    // Verificar se o clique foi em uma área que deve permitir arrasto
    // Permitir arrasto se for em qualquer área vazia do quadro
    const isClickOnDraggableArea = (
      // Clique direto no container principal
      e.target === e.currentTarget ||
      target.classList.contains('kanban-board-container') ||
      
      // Áreas vazias dentro das colunas
      target.classList.contains('droppable-area') ||
      
      // Espaços entre colunas ou áreas vazias
      target.classList.contains('p-3') ||
      target.classList.contains('flex-grow') ||
      
      // Áreas vazias com mensagem "Arraste tarefas aqui"
      target.classList.contains('flex-col') && target.classList.contains('items-center') ||
      
      // Verificar se o pai é uma área arrástavel
      (target.parentElement && 
        (target.parentElement.classList.contains('droppable-area') ||
         target.parentElement.classList.contains('kanban-board-container')))
    );
    
    // Não permitir arrasto em elementos interativos ou que precisam de clique
    const isClickOnInteractiveElement = (
      // Cards e elementos interativos
      target.closest('.kanban-card') ||
      target.closest('button') ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.tagName === 'A' ||
      
      // Elementos de drag and drop
      target.hasAttribute('draggable') ||
      target.closest('[draggable="true"]') ||
      
      // Verificar se tem algum manipulador de clique
      target.onclick ||
      target.getAttribute('onClick')
    );
    
    // Iniciar o arrasto se for em uma área válida e não for um elemento interativo
    if ((isClickOnDraggableArea || !isClickOnInteractiveElement) && !target.closest('.kanban-card')) {
      setIsDraggingScroll(true);
      setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0));
      setScrollLeft(scrollContainerRef.current?.scrollLeft || 0);
      
      // Mudar o cursor para indicar que o arrasto está ativo
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grabbing';
      }
      
      // Prevenir comportamento padrão para evitar conflitos
      e.preventDefault();
    }
  };

  // Manipulador para o movimento durante o arrasto
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingScroll) return;
    
    // Prevenir seleção de texto durante o arrasto
    e.preventDefault();
    
    // Calcular a nova posição de scroll
    const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 1.5; // Multiplicador para ajustar a velocidade do scroll
    
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  // Manipulador para finalizar o arrasto
  const handleMouseUp = () => {
    setIsDraggingScroll(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  // Manipulador para quando o mouse sai da área
  const handleMouseLeave = () => {
    if (isDraggingScroll) {
      setIsDraggingScroll(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grab';
      }
    }
  };

  // Adicionar e remover event listeners
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.style.cursor = 'grab';
    }

    // Limpar event listeners quando o componente for desmontado
    return () => {
      if (scrollContainer) {
        scrollContainer.style.cursor = '';
      }
    };
  }, []);

  const fecharDetalhes = () => {
    setModalAberto(false);
    setTarefaSelecionada(null);
  };

  // Função para determinar a cor do cabeçalho da coluna
  const getColumnHeaderColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'solicitado':
        return 'bg-bravo-dark';
      case 'validação':
        return 'bg-gray-700';
      case 'planejamento':
        return 'bg-gray-600';
      case 'a iniciar':
        return 'bg-teal-500'; // Cor para a nova coluna "A iniciar"
      case 'em execução':
        return 'bg-bravo-secondary';
      case 'aguardando aprovação':
        return 'bg-gray-500';
      case 'em ajustes':
        return 'bg-gray-600';
      case 'finalizado':
        return 'bg-bravo-primary';
      case 'cancelado':
        return 'bg-gray-400';
      case 'pausado':
        return 'bg-gray-500';
      default:
        return 'bg-bravo-secondary';
    }
  };

  // Efeito para atualizar o título da página
  useEffect(() => {
    document.title = `BravoHub - Kanban Global (Admin)`;
  }, []);

  // Verificar se o usuário é admin
  useEffect(() => {
    if (usuario && usuario.tipo !== 'admin') {
      toast.error('Acesso restrito a administradores');
      navigate('/kanban');
    }
  }, [usuario, navigate]);

  // Carregar tarefas ao iniciar
  useEffect(() => {
    if (usuario && usuario.tipo === 'admin') {
      recarregarTarefas();
    }
  }, [usuario?.id]);

  // Função para atualizar os filtros temporários (não aplica automaticamente)
  const handleFiltroChange = (campo: string, valor: any) => {
    setFiltrosTemp({ ...filtrosTemp, [campo]: valor });
  };

  // Função para aplicar os filtros
  const aplicarFiltros = () => {
    console.log('📤 Aplicando filtros:', filtrosTemp);
    setFiltros(filtrosTemp);
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    const filtrosLimpos = {
      pagina: 1,
      limite: 50,
      carregarConcluidas: true,
      diasConcluidas: 7
    };
    setFiltrosTemp(filtrosLimpos);
    setFiltros(filtrosLimpos);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="kanban-header flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-bravo-dark">
          Kanban Global (Admin) - Visão de Todas as Tarefas
        </h2>
        <div className="flex space-x-2">
          <Tooltip title="Atualizar tarefas">
            <IconButton 
              onClick={() => recarregarTarefas()} 
              color="primary"
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filtros">
            <IconButton 
              onClick={() => setMostrarFiltros(!mostrarFiltros)} 
              color="primary"
              size="small"
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      
      {mostrarFiltros && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField
            label="Busca por texto"
            variant="outlined"
            size="small"
            value={filtrosTemp.texto || ''}
            onChange={(e) => handleFiltroChange('texto', e.target.value)}
            placeholder="Buscar em título ou descrição"
            fullWidth
          />
          
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filtrosTemp.status || ''}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Pendente">Pendente</MenuItem>
              <MenuItem value="A iniciar">A iniciar</MenuItem>
              <MenuItem value="Em Andamento">Em Andamento</MenuItem>
              <MenuItem value="Aguardando Aprovação">Aguardando Aprovação</MenuItem>
              <MenuItem value="Ajustes Requisitados">Ajustes Requisitados</MenuItem>
              <MenuItem value="Concluída">Concluída</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Prioridade</InputLabel>
            <Select
              value={filtrosTemp.prioridade || ''}
              onChange={(e) => handleFiltroChange('prioridade', e.target.value)}
              label="Prioridade"
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="baixa">Baixa</MenuItem>
              <MenuItem value="media">Média</MenuItem>
              <MenuItem value="alta">Alta</MenuItem>
              <MenuItem value="urgente">Urgente</MenuItem>
            </Select>
          </FormControl>
          
          <Autocomplete
            options={usuariosDropdown}
            loading={carregandoOpcoes}
            getOptionLabel={(option) => `${option.nome} (${option.id})`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={usuariosDropdown.find(u => u.id === filtrosTemp.responsavel) || null}
            onChange={(_, val) => handleFiltroChange('responsavel', val ? val.id : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Responsável"
                variant="outlined"
                size="small"
                placeholder="Buscar por nome ou ID"
              />
            )}
          />
          
          <Autocomplete
            options={usuariosDropdown}
            loading={carregandoOpcoes}
            getOptionLabel={(option) => `${option.nome} (${option.id})`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={usuariosDropdown.find(u => u.id === filtrosTemp.criador) || null}
            onChange={(_, val) => handleFiltroChange('criador', val ? val.id : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Criador"
                variant="outlined"
                size="small"
                placeholder="Buscar por nome ou ID"
              />
            )}
          />
          
          <Autocomplete
            options={departamentos}
            loading={carregandoOpcoes}
            getOptionLabel={(option) => `${option.nome} (${option.id})`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={departamentos.find(d => d.id === filtrosTemp.departamento) || null}
            onChange={(_, val) => handleFiltroChange('departamento', val ? val.id : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Departamento"
                variant="outlined"
                size="small"
                placeholder="Buscar por nome ou ID"
              />
            )}
          />
          
          <div className="md:col-span-3 flex justify-end space-x-2">
            <Button 
              variant="outlined" 
              onClick={limparFiltros}
            >
              Limpar Filtros
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={aplicarFiltros}
            >
              Buscar
            </Button>
          </div>
        </div>
      )}
      
      <div className="flex items-center mb-4 justify-between">
        <div>
          <span className="text-sm text-gray-600">
            Total de tarefas: {totalTarefas} | Exibindo: {tarefas.length}
          </span>
        </div>
        
        <Button
          variant="outlined"
          onClick={() => recarregarTarefas()}
        >
          Atualizar
        </Button>
      </div>
      
      <DragDropContext onDragEnd={onDragEnd}>
        <div 
          ref={scrollContainerRef}
          className={`flex overflow-x-auto pb-6 h-full kanban-board-container ${isDraggingScroll ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ 
            minHeight: 'calc(100vh - 220px)',
            scrollBehavior: 'smooth',
            userSelect: 'none', // Previne seleção de texto durante o arrasto
            gap: '16px', // Espaço entre as colunas para facilitar o arrasto
            padding: '8px' // Padding adicional para facilitar o arrasto nas bordas
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {quadro.colunas.map((coluna) => (
            <div 
              key={coluna.id} 
              className="flex-shrink-0 w-80 mx-2 first:ml-0 last:mr-0"
            >
              <div className="bg-white rounded-lg shadow-card h-full flex flex-col border border-gray-200">
                <div 
                  className={`p-3 font-medium text-white rounded-t-lg flex justify-between items-center ${getColumnHeaderColor(coluna.titulo)}`}
                >
                  <h3 className="font-semibold">{coluna.titulo}</h3>
                  <div className="flex items-center space-x-2">
                    <span className="bg-white text-bravo-dark text-xs font-semibold px-2 py-1 rounded-full">
                      {coluna.tarefas.length}
                    </span>
                  </div>
                </div>
                
                <Droppable droppableId={coluna.id} type="TAREFA">
                  {(provided, snapshot) => {
                    return (
                      <div 
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`p-3 flex-grow overflow-y-auto droppable-area ${snapshot.isDraggingOver ? 'bg-gray-50' : ''}`}
                        style={{ minHeight: '200px', maxHeight: 'calc(100vh - 220px)' }}
                      >
                        {coluna.tarefas.length === 0 && (
                          <div className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-200 rounded-md text-gray-400 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Sem tarefas
                          </div>
                        )}
                        {coluna.tarefas.map((tarefa, index) => (
                          <Draggable 
                            key={tarefa.id} 
                            draggableId={tarefa.id} 
                            index={index}
                          >
                            {(provided, snapshot) => {
                              return (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => abrirDetalhes(tarefa)}
                                  className={`mb-3 last:mb-0 kanban-card ${snapshot.isDragging ? 'shadow-lg' : ''} transition-shadow`}
                                  style={{
                                    ...provided.draggableProps.style,
                                    opacity: snapshot.isDragging ? 0.8 : 1
                                  }}
                                >
                                  <KanbanCard 
                                    tarefa={tarefa} 
                                    usuarioLogadoId={usuario?.id || ''}
                                    mostrarTarefasDepartamento={true}
                                    tipo="recebidas"
                                  />
                                </div>
                              );
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    );
                  }}
                </Droppable>
              </div>
            </div>
          ))}
        </div>
      </DragDropContext>
      
      {tarefaSelecionada && (
        <TaskDetailsModal 
          tarefa={tarefaSelecionada as Tarefa} 
          aberto={modalAberto} 
          onFechar={fecharDetalhes} 
        />
      )}
      
      {/* Indicador discreto no canto, não bloqueia mais a tela inteira a
          cada atualização (filtro, drag-and-drop, etc.) — antes qualquer
          recarregamento trivial escurecia e travava o quadro inteiro. */}
      {carregando && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white dark:bg-gray-800 shadow-lg rounded-full px-4 py-2 flex items-center gap-2 border border-gray-200 dark:border-gray-700">
          <Spinner size={18} />
          <span className="text-sm text-gray-700 dark:text-gray-200">Atualizando tarefas...</span>
        </div>
      )}
    </div>
  );
};

export default KanbanAdminBoard;
